import { Account } from '@interfaces/common.interface';
import jwt from 'jsonwebtoken';
import { Map, fromJS } from 'immutable';
import { USER_VALID_STATUS, EMPTY_STRING, EMPTY_GUID } from '@constants/common';

const permissionsAvailableToAll = new Set([
  'USER_POST_USER_PUBLIC_CREATE',
  'USER_POST_USER_PUBLIC_DELETE',
  'USER_POST_USER_PUBLIC_RETRIEVE',
  'USER_POST_USER_PUBLIC_LIST',
  'USER_POST_USER_PUBLIC_UPDATE',
  'USER_POST_USER_PUBLIC_LINK',
  'USER_POST_USER_PUBLIC_DELINK',
  'USER_POST_USER_PUBLIC_CHANGE_ORG',
  'USER_POST_USER_PUBLIC_CREATE_OTP',
  'USER_POST_USER_INTERNAL_CREATE',
  'USER_POST_USER_INTERNAL_RETRIEVE',
  'USER_POST_USER_INTERNAL_SWITCH',
  'USER_POST_USER_INTERNAL_LIST',
  'USER_POST_USER_INTERNAL_UPDATE',
  'USER_POST_USER_INTERNAL_DELETE',
  'USER_POST_USER_INTERNAL_STATISTIC',
  'TAASCONFIG_POST_TAASCONFIG_CREATE',
  'TAASCONFIG_POST_TAASCONFIG_RETRIEVE',
  'TAASCONFIG_POST_TAASCONFIG_LIST',
  'TAASCONFIG_POST_TAASCONFIG_UPDATE',
  'TAASCONFIG_POST_TAASCONFIG_DELETE',
  'TAASCONFIG_POST_SCOUTAI_GRADUATE',
  'TAASCONFIG_POST_SCOUTAIRESULT_ARTICLE_RESULT',
  'SCOUTAI_SCOUTMASTER_POST_SCOUTAI_CREATE',
  'SCOUTAI_SCOUTMASTER_POST_SCOUTAI_LIST',
  'SCOUTAI_SCOUTMASTER_POST_SCOUTAI_UPDATE',
  'SHARE_SCOUTAI_POST_SHARE_SCOUTAI_TO_USER',
  'APIM_SUBSCRIPTION_CREATE',
  'APIM_SUBSCRIPTION_UPDATESTATUS',
  'APIM_RENEW_KEY',
  'APIM_SUBSCRIPTION_LIST',
  'SCOUTAI_POST_SCOUTAI_CREATE',
  'SCOUTAI_POST_SCOUTAI_RETRIEVE',
  'SCOUTAI_POST_SCOUTAI_LIST',
  'SCOUTAI_POST_SCOUTAI_UPDATE',
  'SCOUTAI_POST_SCOUTAI_DELETE',
  'SCOUTAI_POST_SHARE_SCOUTAI',
  'SCOUTAI_POST_ACCEPT_SHARE_SCOUTAI',
  'SCOUTAI_POST_SCOUTAIRESULT_INITIAL_TRAINING',
  'SCOUTAI_POST_SCOUTAIRESULT_CONTINUOUS_TRAINING',
  'SCOUTAI_POST_SCOUTAIRESULT_ARTICLE_TRAINING',
  'SCOUTAI_POST_SCOUTAIRESULT_TRAINING',
  'SCOUTAI_POST_SCOUTAIRESULT_RETRIEVE',
  'SCOUTAI_POST_SCOUTAIRESULT_ARTICLE_TRAINING_REMOVE',
  'SCOUTAI_POST_MODIFY_SCOUTAI_MODEL_PARAMETER',
  'SCOUTAI_SEER_PREDICTION_ADD',
  'SCOUTAI_SEER_PREDICTION_PEEK_STATUS',
  'SCOUTAI_SEER_PREDICTION_RETRIEVE',
  'SCOUTAI_POST_CONVERT_CUSTOM_SCOUT',
  'NERL_CORRECTION_POST_SUBMIT',
  'NERL_CORRECTION_POST_RETRIEVE',
  'NERL_CORRECTION_POST_DELETE',
  'SCOUTAI_SCOUTMASTER_POST_SCOUTAI_RETRIEVE',
]);

const isValidUser = (user: any) => {
  if (user)
    return true;
  return false;
};

const requiresPasswordChange = (account: any): boolean => {
  if (!account || account.isPasswordChange === undefined || account.isPasswordChange === null) {
    return false;
  }

  return account.isPasswordChange === false || account.isPasswordChange === 'false';
};

const hasOrgAccess = (account: Account, orgGuid: string) => {
  let hasOrgAcces = false;
  if (!orgGuid) {
    return true;
  }
  account?.subcriptionFeaturePermissionModel?.forEach(sub => {
    if (sub.organisationGuid === orgGuid) {
      hasOrgAcces = true;
    }
  });

  return hasOrgAcces;
};

const permissionExist = (accountMap, permission: string, orgGuid: string = EMPTY_STRING) => {
  if (!permission || permissionsAvailableToAll.has(permission)) {
    return true;
  }
  let hasPermission = false;
  const account = accountMap.toJS();
  const orgLists = account.subcriptionFeaturePermissionModel;
  if (orgGuid && account.isPublicUser) {
    const currentOrg = orgLists.find(org => org.organisationGuid === orgGuid);
    hasPermission = currentOrg.permissionList.find(perm => perm === permission);
  } else if (orgLists?.length > 0) {
    const currentOrg = orgLists.find(org => org.organisationGuid === EMPTY_GUID);
    hasPermission = currentOrg.permissionList.find(perm => perm === permission);
  }

  return !!hasPermission;
};

const checkPasswordUpper = (password: string): boolean => {
  const upper = new RegExp('[A-Z]+');
  if (password.match(upper)) {
    return true;
  }
  return false;
};

const checkPasswordLower = (password: string): boolean => {
  const lower = new RegExp('[a-z]+');
  if (password.match(lower)) {
    return true;
  }
  return false;
};

const checkPasswordNumber = (password: string): boolean => {
  const number = new RegExp('[0-9]+');
  if (password.match(number)) {
    return true;
  }
  return false;
};

const checkPasswordSpecialCharacter = (password: string) : boolean => {
  const special = new RegExp('[^A-Za-z0-9]+');
  if (password.match(special)) {
    return true;
  }
  return false;
};

const validPassword = (password: string): boolean => {
  const hasUpper = checkPasswordUpper(password);
  const hasLower = checkPasswordLower(password);
  const hasNumber = checkPasswordNumber(password);
  const hasSpecial = checkPasswordSpecialCharacter(password);
  const validLength = password.length >= 8 && password.length <= 20;

  return hasUpper && hasLower && hasNumber && validLength && hasSpecial;
};

const getAccountState = (user: any): any => {
  if (user) {
    return fromJS(user);
  }
  return Map({});
};

export { getAccountState, isValidUser, requiresPasswordChange, hasOrgAccess, permissionExist, checkPasswordUpper, checkPasswordLower, checkPasswordNumber, checkPasswordSpecialCharacter, validPassword };
